export interface ProgrammaticFaq {
  question: string;
  answer: string;
}

export interface ProgrammaticVertical {
  slug: string;
  industry: string;
  title: string;
  bluf: string;
  summary: string;
  avgTicketUsd: number;
  benchmarkLiftPercent: number;
  monthlyExampleUsd: number;
  buyerIntentSignals: string[];
  keywords: string[];
  faqs: ProgrammaticFaq[];
}

export interface MarketCity {
  slug: string;
  city: string;
  state: string;
  region: string;
  buyerBehavior: string;
  seasonalityNote: string;
  leadWindowNote: string;
}

export interface CityVerticalRouteConfig {
  verticalSlug: string;
  citySlug: string;
  localDemandDriver: string;
  localSalesMoment: string;
  outreachTip: string;
  recommendedChannels: string[];
  qualityScore: number;
}

export interface CityVerticalPageContent {
  localOpportunitySummary: string;
  objectionHandling: Array<{ objection: string; response: string }>;
  launchChecklist: string[];
  messagingAngles: string[];
  localMetrics: {
    typicalDecisionWindowDays: number;
    mobileCheckoutSharePercent: number;
    repeatVisitClosePercent: number;
  };
  commonFinancedPurchases: string[];
}

export const PROGRAMMATIC_VERTICALS: ProgrammaticVertical[] = [
  {
    slug: 'vintage-furniture-financing',
    industry: 'Vintage Furniture',
    title: 'Financing for Vintage Furniture Sellers',
    bluf: 'Vintage furniture financing helps buyers afford statement pieces with predictable monthly payments while sellers get paid upfront through Stripe checkout. For independent shops, this removes sticker shock at the exact decision moment and increases same-day close rates without building an in-house financing stack.',
    summary: 'Purpose-built for independent furniture shops selling moderate to high-ticket pieces in person, over DM, or through direct links.',
    avgTicketUsd: 1800,
    benchmarkLiftPercent: 23.7,
    monthlyExampleUsd: 159,
    buyerIntentSignals: ['Quote viewed twice in 24h', 'Checkout opened from QR in showroom', 'Buyer returned from saved link'],
    keywords: ['vintage furniture financing', 'furniture payment plans for small business', 'monthly payment options for furniture'],
    faqs: [
      {
        question: 'Can small furniture stores offer financing without Shopify?',
        answer: 'Yes. LendLifted uses Stripe-powered checkout links and QR flow, so sellers can offer financing options without migrating to a commerce platform.',
      },
      {
        question: 'Does the seller wait for monthly installments?',
        answer: 'No. When eligible payment methods are used, the seller is paid through normal Stripe settlement while the buyer repays over time through their selected provider.',
      },
    ],
  },
  {
    slug: 'tattoo-financing',
    industry: 'Tattoo Studios',
    title: 'Financing for Tattoo Studios',
    bluf: 'Tattoo financing lets clients start premium work with monthly options instead of delaying for budget reasons. Studios can send one financing-enabled checkout link after consultation, helping clients commit faster while artists focus on craft and calendar utilization.',
    summary: 'Designed for consult-driven studios that sell premium sleeves, multi-session work, and custom projects.',
    avgTicketUsd: 2400,
    benchmarkLiftPercent: 21.4,
    monthlyExampleUsd: 212,
    buyerIntentSignals: ['Consult completed but no deposit', 'Quote reopened after hours', 'Checkout started from SMS follow-up'],
    keywords: ['tattoo financing', 'finance a sleeve tattoo', 'tattoo payment plans'],
    faqs: [
      {
        question: 'Can financing be offered during in-person consultation?',
        answer: 'Yes. Studios can show a QR code on phone and let buyers complete checkout privately on their own device.',
      },
      {
        question: 'Will monthly option messaging feel pushy?',
        answer: 'No. Presenting clear monthly choices often reduces friction and helps buyers decide with more confidence.',
      },
    ],
  },
  {
    slug: 'contractor-financing',
    industry: 'Contractors',
    title: 'Financing for Contractors and Remodelers',
    bluf: 'Contractor financing gives homeowners pay-over-time options right when quotes are presented, reducing delay between estimate and signed agreement. Sellers share one financing-enabled link and track buyer intent so follow-ups happen when purchase intent is strongest.',
    summary: 'Built for remodeling, roofing, HVAC, and home-service teams closing larger jobs in competitive markets.',
    avgTicketUsd: 9800,
    benchmarkLiftPercent: 26.1,
    monthlyExampleUsd: 867,
    buyerIntentSignals: ['Quote reopened after a financing question', 'Checkout reached payment method step', 'SMS reminder triggered second session'],
    keywords: ['contractor financing', 'home improvement payment plans', 'HVAC financing options'],
    faqs: [
      {
        question: 'Is this only for large teams?',
        answer: 'No. Owner-operator teams use LendLifted from mobile during onsite quoting and kitchen-table closes.',
      },
      {
        question: 'Can reps track which follow-up message performs best?',
        answer: 'Yes. Seller Studio highlights signals and follow-up impact so your team improves close behavior over time.',
      },
    ],
  },
  {
    slug: 'med-spa-financing',
    industry: 'Med Spas',
    title: 'Financing for Med Spas',
    bluf: 'Med spa financing helps clients start higher-value treatment plans with manageable monthly options, while providers keep checkout secure and professional. Financing-enabled links reduce drop-off after consultation and make premium packages more accessible.',
    summary: 'Optimized for consult-heavy wellness services where timing and trust determine conversion.',
    avgTicketUsd: 3200,
    benchmarkLiftPercent: 19.8,
    monthlyExampleUsd: 283,
    buyerIntentSignals: ['Treatment quote viewed from mobile', 'Checkout started after reminder', 'Second visit before payment'],
    keywords: ['med spa financing', 'aesthetic treatment payment plans', 'wellness financing checkout'],
    faqs: [
      {
        question: 'Do buyers need to apply in front of staff?',
        answer: 'No. They can complete checkout privately through link or QR on their own device.',
      },
      {
        question: 'Can we use this for package-based offers?',
        answer: 'Yes. Teams commonly use financing for bundled care plans and higher-ticket treatment programs.',
      },
    ],
  },
  {
    slug: 'dental-financing',
    industry: 'Dental Practices',
    title: 'Financing for Dental Practices',
    bluf: 'Dental financing supports treatment acceptance by replacing upfront payment stress with clear monthly options. Practices can generate financing-enabled checkout quickly, share it by SMS, and monitor intent signals to improve treatment plan conversion without extra administrative burden.',
    summary: 'Created for treatment coordinators and practice owners who need clarity, speed, and trust.',
    avgTicketUsd: 5400,
    benchmarkLiftPercent: 18.9,
    monthlyExampleUsd: 478,
    buyerIntentSignals: ['Treatment plan link reopened', 'Checkout started then paused', 'Buyer returned via reminder link'],
    keywords: ['dental financing software', 'treatment plan payment options', 'dental monthly payments'],
    faqs: [
      {
        question: 'Does this replace our practice management software?',
        answer: 'No. LendLifted focuses on financing-enabled checkout and conversion flow, not core EHR or scheduling systems.',
      },
      {
        question: 'Will this work for elective procedures?',
        answer: 'Yes. Elective and high-ticket procedures are a common use case for monthly payment presentation.',
      },
    ],
  },
  {
    slug: 'custom-jewelry-financing',
    industry: 'Custom Jewelry',
    title: 'Financing for Custom Jewelry Sellers',
    bluf: 'Custom jewelry financing helps shoppers commit to meaningful pieces with monthly payment flexibility. Sellers can close bespoke orders faster by sharing a financing-enabled link during consultation and reducing hesitation around upfront spend.',
    summary: 'Great for independent jewelers and custom design studios with high-consideration purchases.',
    avgTicketUsd: 2600,
    benchmarkLiftPercent: 22.3,
    monthlyExampleUsd: 229,
    buyerIntentSignals: ['Design consultation completed', 'Link opened from text after appointment', 'Checkout resumed after reminder'],
    keywords: ['custom jewelry financing', 'engagement ring payment plans', 'jewelry pay over time'],
    faqs: [
      {
        question: 'Can we collect deposits and offer financing on remaining balance?',
        answer: 'Yes. Sellers can structure quote totals and optional deposits, then share checkout links for completion.',
      },
      {
        question: 'Is this useful for in-store and remote sales?',
        answer: 'Yes. Teams use QR in-store and links for remote follow-up conversations.',
      },
    ],
  },
  {
    slug: 'photography-financing',
    industry: 'Photography Studios',
    title: 'Financing for Photography Studios',
    bluf: 'Photography financing improves close rates on premium packages by giving clients monthly options at proposal time. Studios can keep momentum from inquiry to booking by sharing financing-enabled checkout links and reducing budget-driven delays.',
    summary: 'Best for wedding, portrait, and commercial packages where budgets are planned across months.',
    avgTicketUsd: 3500,
    benchmarkLiftPercent: 20.6,
    monthlyExampleUsd: 309,
    buyerIntentSignals: ['Proposal opened multiple times', 'Checkout initiated after weekend', 'Buyer resumed from follow-up text'],
    keywords: ['photography payment plans', 'wedding photography financing', 'studio financing checkout'],
    faqs: [
      {
        question: 'Can financing be offered directly in proposal follow-up?',
        answer: 'Yes. You can send financing-enabled links by SMS or email as part of proposal workflow.',
      },
      {
        question: 'Is this only for weddings?',
        answer: 'No. Studios use financing for portrait memberships, commercial retainers, and destination packages.',
      },
    ],
  },
  {
    slug: 'fitness-coaching-financing',
    industry: 'Fitness and Coaching',
    title: 'Financing for Fitness and Coaching Programs',
    bluf: 'Coaching and fitness financing helps clients enroll in transformational programs by spreading costs into monthly options. Teams can maintain momentum from consultation call to checkout with one financing-enabled payment link and behavior-based follow-up.',
    summary: 'Designed for independent coaches and boutique programs selling high-value outcomes.',
    avgTicketUsd: 2100,
    benchmarkLiftPercent: 17.5,
    monthlyExampleUsd: 185,
    buyerIntentSignals: ['Call recap link opened', 'Checkout started from mobile', 'Re-engagement after follow-up nudge'],
    keywords: ['coaching payment plans', 'fitness program financing', 'online coaching monthly payments'],
    faqs: [
      {
        question: 'Can this work for remote coaching sales?',
        answer: 'Yes. The full flow is link-based, so buyers can review and complete checkout from anywhere.',
      },
      {
        question: 'Will this support recurring offer structures?',
        answer: 'Yes. Teams can present financing-enabled checkout for higher-ticket programs and maintain simple close workflows.',
      },
    ],
  },
];

export const getProgrammaticVerticalBySlug = (slug: string): ProgrammaticVertical | undefined =>
  PROGRAMMATIC_VERTICALS.find((vertical) => vertical.slug === slug);

export const MARKET_CITIES: MarketCity[] = [
  {
    slug: 'austin-tx',
    city: 'Austin',
    state: 'TX',
    region: 'Central Texas',
    buyerBehavior: 'Buyers respond quickly to text-first checkout and often decide after seeing monthly payment options side by side.',
    seasonalityNote: 'Spring and early summer usually drive stronger home upgrades and studio bookings.',
    leadWindowNote: 'Most high-intent buyers return to the link within 24 to 72 hours.',
  },
  {
    slug: 'dallas-tx',
    city: 'Dallas',
    state: 'TX',
    region: 'North Texas',
    buyerBehavior: 'Buyers compare several providers, so speed-to-quote and clear monthly pricing strongly influence close rate.',
    seasonalityNote: 'Quarter-end promos and summer install windows often create stronger conversion periods.',
    leadWindowNote: 'Fast same-day follow-up typically performs best after quote delivery.',
  },
  {
    slug: 'miami-fl',
    city: 'Miami',
    state: 'FL',
    region: 'South Florida',
    buyerBehavior: 'Mobile-first buyers often continue checkout later in the day after in-person conversations.',
    seasonalityNote: 'Tourism and seasonal resident cycles can create demand spikes for premium services.',
    leadWindowNote: 'Short reminder cadence over 48 hours often outperforms long wait times.',
  },
  {
    slug: 'new-york-ny',
    city: 'New York',
    state: 'NY',
    region: 'New York Metro',
    buyerBehavior: 'Decision speed is high when quotes are concise and payment options are immediately visible.',
    seasonalityNote: 'Holiday and event-driven spending periods can raise average ticket sizes.',
    leadWindowNote: 'Buyers often reopen links multiple times before committing.',
  },
  {
    slug: 'los-angeles-ca',
    city: 'Los Angeles',
    state: 'CA',
    region: 'Southern California',
    buyerBehavior: 'Consultative selling works well when teams pair visual examples with monthly payment clarity.',
    seasonalityNote: 'Campaign launches and seasonal service packages frequently influence conversion cycles.',
    leadWindowNote: 'Follow-up on day one and day three usually catches most ready buyers.',
  },
  {
    slug: 'chicago-il',
    city: 'Chicago',
    state: 'IL',
    region: 'Greater Chicago',
    buyerBehavior: 'Buyers value predictability, making fixed monthly framing effective for larger-ticket services.',
    seasonalityNote: 'Weather-linked service demand creates clear peaks for home and wellness categories.',
    leadWindowNote: 'Three-touch follow-up plans within a week tend to perform consistently.',
  },
  {
    slug: 'phoenix-az',
    city: 'Phoenix',
    state: 'AZ',
    region: 'Greater Phoenix',
    buyerBehavior: 'Fast quoting and instant link sharing is especially effective for in-the-field service teams.',
    seasonalityNote: 'Extreme-weather cycles often raise urgency for home and repair services.',
    leadWindowNote: 'Buyers frequently convert after a second view of financing options.',
  },
  {
    slug: 'nashville-tn',
    city: 'Nashville',
    state: 'TN',
    region: 'Middle Tennessee',
    buyerBehavior: 'Word-of-mouth heavy markets benefit from clear, low-friction checkout links buyers can forward.',
    seasonalityNote: 'Event and relocation trends can lift demand for premium custom work.',
    leadWindowNote: 'Helpful reminders within two business days keep momentum high.',
  },
];

export const getMarketCityBySlug = (slug: string): MarketCity | undefined =>
  MARKET_CITIES.find((city) => city.slug === slug);

const VERTICAL_DEMAND_DRIVERS: Record<string, string[]> = {
  'vintage-furniture-financing': [
    'design-forward home upgrades and boutique showroom traffic',
    'custom interior projects with higher average basket size',
    'buyers discovering one-of-a-kind pieces and needing payment flexibility',
  ],
  'tattoo-financing': [
    'premium multi-session packages and long-form custom work',
    'same-day consult decisions where monthly framing reduces hesitation',
    'clients stretching to book higher-value art they truly want',
  ],
  'contractor-financing': [
    'urgent repair and upgrade demand tied to weather and home wear',
    'larger remodel bids where buyers compare several options',
    'in-home quotes where affordability clarity speeds commitment',
  ],
  'med-spa-financing': [
    'high-value treatment plans purchased after consultation',
    'bundle-based offers with meaningful upfront sticker shock',
    'buyers who convert faster when monthly options are visible early',
  ],
  'dental-financing': [
    'elective treatments with high treatment-plan drop-off risk',
    'care decisions that improve when payment options are transparent',
    'coordinator workflows that benefit from immediate checkout links',
  ],
};

const DEFAULT_DEMAND_DRIVERS = [
  'high-ticket offers where monthly options reduce upfront friction',
  'consult-based selling moments where speed and clarity win',
  'buyers who need payment flexibility before saying yes',
];

const VERTICAL_SALES_MOMENTS: Record<string, string[]> = {
  'vintage-furniture-financing': ['in-showroom walkthrough close', 'post-design recommendation handoff'],
  'tattoo-financing': ['end-of-consult booking window', 'design approval checkpoint'],
  'contractor-financing': ['kitchen-table estimate review', 'post-scope confirmation follow-up'],
  'med-spa-financing': ['treatment-plan consult close', 'front-desk package acceptance'],
  'dental-financing': ['treatment coordinator handoff', 'chairside next-step decision'],
};

const DEFAULT_SALES_MOMENTS = ['proposal approval moment', 'post-quote follow-up window'];

const VERTICAL_OUTREACH_TIPS: Record<string, string[]> = {
  'vintage-furniture-financing': [
    'Send a clean photo, total price, and monthly option in one concise message.',
    'Keep copy visual and simple so buyers can decide fast without extra back-and-forth.',
  ],
  'tattoo-financing': [
    'Present monthly options right after consult while motivation is high.',
    'Use short confidence-first language focused on booking next steps.',
  ],
  'contractor-financing': [
    'Lead follow-up with monthly affordability, then project timeline.',
    'Use same-day and 48-hour reminders to keep momentum.',
  ],
  'med-spa-financing': [
    'Use privacy-forward language and clear package framing.',
    'Tie monthly options to outcomes buyers care about.',
  ],
  'dental-financing': [
    'Give patients a one-page summary with monthly options before they leave.',
    'Keep language straightforward and reassurance-focused.',
  ],
};

const DEFAULT_OUTREACH_TIPS = [
  'Keep follow-up short, clear, and centered on monthly affordability.',
  'Use one direct CTA and avoid sending too much information at once.',
];

const CHANNEL_LIBRARY = [
  'SMS checkout link',
  'In-person QR handoff',
  'Email quote recap',
  'Customer portal message',
  'Sales rep call + text handoff',
  'Instagram/DM follow-up',
];

const OBJECTION_LIBRARY = [
  {
    objection: 'I need time to think about the total price.',
    response: 'Show the full total and a monthly option side by side so the decision feels concrete and manageable.',
  },
  {
    objection: 'I am comparing a few providers first.',
    response: 'Share your checkout link quickly and make next steps clear so buyers do not lose momentum.',
  },
  {
    objection: 'I am not sure this is the right time financially.',
    response: 'Use a transparent deposit plus monthly structure to reduce upfront pressure.',
  },
  {
    objection: 'I do not want a complicated application process.',
    response: 'Position checkout as a simple phone flow that can be completed privately in minutes.',
  },
];

const CHECKLIST_LIBRARY = [
  'Create a quote with clear total, optional deposit, and monthly framing.',
  'Share checkout by the buyer-preferred channel before they leave the conversation.',
  'Send one same-day reminder if checkout is opened but not completed.',
  'Use follow-up copy that repeats value and monthly clarity, not pressure.',
  'Track which channels close fastest and standardize your best sequence.',
];

const ANGLE_LIBRARY = [
  '“Get started now and spread the cost over time.”',
  '“You can choose a monthly option that fits your budget.”',
  '“Simple checkout link, no complicated paperwork on your side.”',
  '“See your options clearly before making a final decision.”',
  '“Move forward today while keeping cash flow predictable.”',
];

const VERTICAL_PURCHASE_LIBRARY: Record<string, string[]> = {
  'vintage-furniture-financing': [
    'Dining table sets',
    'Statement sofas',
    'Hand-restored dressers',
    'Custom upholstery bundles',
    'Lighting and decor bundles',
  ],
  'tattoo-financing': [
    'Full-sleeve packages',
    'Multi-session custom work',
    'Cover-up projects',
    'Large black-and-grey pieces',
    'Studio membership packages',
  ],
  'contractor-financing': [
    'Kitchen remodel projects',
    'HVAC replacements',
    'Roof repairs and replacements',
    'Bathroom upgrades',
    'Window and door packages',
  ],
  'med-spa-financing': [
    'Injectable treatment bundles',
    'Laser package plans',
    'Body contouring programs',
    'Skin rejuvenation packages',
    'Membership treatment plans',
  ],
  'dental-financing': [
    'Implant treatment plans',
    'Orthodontic packages',
    'Cosmetic smile makeovers',
    'Full restorative treatments',
    'Oral surgery procedures',
  ],
  'custom-jewelry-financing': [
    'Custom engagement rings',
    'Wedding band sets',
    'Heirloom redesign projects',
    'Bespoke pendant orders',
    'Anniversary jewelry pieces',
  ],
  'photography-financing': [
    'Wedding photo/video packages',
    'Destination session bundles',
    'Commercial brand shoots',
    'Premium portrait collections',
    'Album and print upgrades',
  ],
  'fitness-coaching-financing': [
    '12-week transformation programs',
    'Executive coaching packages',
    'Hybrid online/in-person plans',
    'Performance coaching bundles',
    'Small-group premium programs',
  ],
};

const hashString = (value: string): number => {
  let hash = 0;
  for (let i = 0; i < value.length; i += 1) {
    hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
  }
  return hash;
};

const pickBySeed = <T>(items: T[], seed: number, offset = 0): T => items[(seed + offset) % items.length];

const uniquePicksBySeed = <T>(items: T[], seed: number, count: number): T[] => {
  if (items.length <= count) return items;
  const start = seed % items.length;
  const result: T[] = [];
  for (let i = 0; i < count; i += 1) {
    result.push(items[(start + i) % items.length]);
  }
  return result;
};

export const getCityVerticalConfig = (
  verticalSlug: string,
  citySlug: string
): CityVerticalRouteConfig | undefined => {
  const vertical = getProgrammaticVerticalBySlug(verticalSlug);
  const city = getMarketCityBySlug(citySlug);
  if (!vertical || !city) return undefined;
  const seed = hashString(`${verticalSlug}:${citySlug}`);
  const drivers = VERTICAL_DEMAND_DRIVERS[verticalSlug] || DEFAULT_DEMAND_DRIVERS;
  const salesMoments = VERTICAL_SALES_MOMENTS[verticalSlug] || DEFAULT_SALES_MOMENTS;
  const outreachTips = VERTICAL_OUTREACH_TIPS[verticalSlug] || DEFAULT_OUTREACH_TIPS;
  const recommendedChannels = uniquePicksBySeed(CHANNEL_LIBRARY, seed, 3);

  return {
    verticalSlug,
    citySlug,
    localDemandDriver: pickBySeed(drivers, seed, 0),
    localSalesMoment: pickBySeed(salesMoments, seed, 1),
    outreachTip: pickBySeed(outreachTips, seed, 2),
    recommendedChannels,
    qualityScore: 88 + (seed % 10),
  };
};

export const isProgrammaticCityRouteEnabled = (verticalSlug: string, citySlug: string): boolean =>
  Boolean(getProgrammaticVerticalBySlug(verticalSlug) && getMarketCityBySlug(citySlug));

export const getProgrammaticCityRouteConfigs = (): CityVerticalRouteConfig[] =>
  PROGRAMMATIC_VERTICALS.flatMap((vertical) =>
    MARKET_CITIES.map((city) => getCityVerticalConfig(vertical.slug, city.slug)).filter(Boolean) as CityVerticalRouteConfig[]
  );

export const getProgrammaticCityRoutes = (): string[] =>
  getProgrammaticCityRouteConfigs().map((config) => `/financing/${config.verticalSlug}/${config.citySlug}`);

export const getCityVerticalPageContent = (
  verticalSlug: string,
  citySlug: string
): CityVerticalPageContent | undefined => {
  const vertical = getProgrammaticVerticalBySlug(verticalSlug);
  const city = getMarketCityBySlug(citySlug);
  const routeConfig = getCityVerticalConfig(verticalSlug, citySlug);
  if (!vertical || !city || !routeConfig) return undefined;

  const seed = hashString(`${verticalSlug}:${citySlug}`);
  const objections = uniquePicksBySeed(OBJECTION_LIBRARY, seed, 3);
  const checklist = uniquePicksBySeed(CHECKLIST_LIBRARY, seed + 2, 4);
  const angles = uniquePicksBySeed(ANGLE_LIBRARY, seed + 4, 3);
  const purchases = uniquePicksBySeed(
    VERTICAL_PURCHASE_LIBRARY[verticalSlug] || [
      'Higher-ticket custom work',
      'Bundled service packages',
      'Large one-time purchases',
      'Consult-driven projects',
      'Premium upgrades',
    ],
    seed + 1,
    3
  );

  const localMetrics = {
    typicalDecisionWindowDays: 2 + (seed % 6),
    mobileCheckoutSharePercent: 52 + (seed % 34),
    repeatVisitClosePercent: 28 + (seed % 31),
  };

  return {
    localOpportunitySummary: `${city.city} sellers in ${vertical.industry.toLowerCase()} often convert faster when monthly options are introduced during ${routeConfig.localSalesMoment}. In this market, ${routeConfig.localDemandDriver} creates strong demand, but buyers still expect flexible payment paths before committing.`,
    objectionHandling: objections,
    launchChecklist: checklist,
    messagingAngles: angles,
    localMetrics,
    commonFinancedPurchases: purchases,
  };
};

export const getRelatedProgrammaticCityRoutes = (
  verticalSlug: string,
  citySlug: string,
  limit = 6
): Array<{ verticalSlug: string; citySlug: string }> =>
  getProgrammaticCityRouteConfigs()
    .filter(
      (config) =>
        !(config.verticalSlug === verticalSlug && config.citySlug === citySlug) &&
        (config.verticalSlug === verticalSlug || config.citySlug === citySlug)
    )
    .sort((a, b) => b.qualityScore - a.qualityScore)
    .slice(0, limit)
    .map((config) => ({ verticalSlug: config.verticalSlug, citySlug: config.citySlug }));
